import { useEffect, useState, type RefObject } from 'react';

/**
 * True once `ref`'s element has been on screen at least once.
 *
 * For a per-row read in a long table: the Tasks list routinely holds a
 * thousand rows and renders them all, so "fetch for every row that
 * needs it" is a thousand requests on open. Gating on this makes it the
 * dozen or so actually in view, then a few more as someone scrolls.
 *
 * LATCHED. It never goes back to false, so a row scrolled away and back
 * keeps whatever it already fetched instead of dropping and re-asking.
 *
 * ONE OBSERVER FOR EVERY ELEMENT, not one per caller: a thousand
 * IntersectionObservers would cost more than the requests they save. It
 * is created on first use and dropped when nothing is waiting, so a
 * test that swaps the global in between gets a fresh one.
 *
 * Where IntersectionObserver does not exist (jsdom, an old WebView) the
 * element counts as seen at once -- the read happens, as it would have
 * without this, rather than never.
 */
let shared: { io: IntersectionObserver; waiting: Map<Element, () => void> } | null = null;

function unwatch(el: Element): void {
  if (!shared || !shared.waiting.delete(el)) return;
  shared.io.unobserve(el);
  if (shared.waiting.size === 0) {
    shared.io.disconnect();
    shared = null;
  }
}

function watch(el: Element, onSeen: () => void): () => void {
  if (!shared) {
    const waiting = new Map<Element, () => void>();
    const io = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const seen = waiting.get(entry.target);
        if (!seen) continue;
        unwatch(entry.target);
        seen();
      }
    });
    shared = { io, waiting };
  }
  shared.waiting.set(el, onSeen);
  shared.io.observe(el);
  return () => unwatch(el);
}

export function useSeenOnce(ref: RefObject<Element | null>, active: boolean): boolean {
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (!active || seen) return undefined;
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === 'undefined') {
      setSeen(true);
      return undefined;
    }
    return watch(el, () => setSeen(true));
  }, [ref, active, seen]);
  return seen;
}
